import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useSiteData } from '../hooks/useSiteData';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { data: settings } = useSiteData('settings');
  const logoUrl = settings?.logo_url;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Glam Booth', path: '/glam-booth' },
    { name: 'Photography', path: '/photography' },
    { name: 'Videography', path: '/videography' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'About', path: '/about' },
    { name: 'FAQs', path: '/faq' },
  ];

  const isActive = (path) =>
    path === '/portfolio'
      ? ['/portfolio', '/previous-events', '/our-work'].includes(location.pathname)
      : location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled ? 'bg-[#1F1A17]/95 backdrop-blur-md shadow-lg' : 'bg-[#1F1A17]/40 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center group" aria-label="Mayden Films home">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Mayden Films"
                className="h-10 sm:h-12 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                data-testid="header-logo-image"
              />
            ) : (
              <div className="text-2xl font-bold tracking-tight" data-testid="header-logo-text">
                <span className="text-[#EADBC8] group-hover:text-[#C5A880] transition-colors duration-300">Mayden</span>
                <span className="text-[#C5A880] ml-1">Films</span>
              </div>
            )}
          </Link>

          <nav className="hidden lg:flex items-center space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-sm font-medium transition-colors duration-300 relative group ${
                  isActive(link.path) ? 'text-[#C5A880]' : 'text-[#EADBC8] hover:text-[#C5A880]'
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-[#C5A880] transition-all duration-300 group-hover:w-full ${
                    isActive(link.path) ? 'w-full' : 'w-0'
                  }`}
                />
              </Link>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link to="/quote">
              <Button
                className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-6 transition-transform duration-300 hover:scale-105"
                data-testid="header-cta-btn"
              >
                Request a Quote
              </Button>
            </Link>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-[#EADBC8] hover:text-[#C5A880] transition-colors duration-300"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      <div
        className={`lg:hidden transition-all duration-300 ease-in-out overflow-hidden ${
          isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-[#1F1A17]/98 backdrop-blur-md px-4 pt-2 pb-6 space-y-2 border-t border-[#C5A880]/20">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block py-3 px-4 text-base font-medium rounded-lg transition-all duration-300 ${
                isActive(link.path)
                  ? 'text-[#C5A880] bg-[#C5A880]/10'
                  : 'text-[#EADBC8] hover:text-[#C5A880] hover:bg-[#C5A880]/5'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <Link to="/quote" onClick={() => setIsMobileMenuOpen(false)}>
            <Button className="w-full bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold mt-4">
              Request a Quote
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
};
